import React, { RefObject } from 'react';
import { Bot, Sparkles } from 'lucide-react';
import { TypingIndicator } from '../TypingIndicator';
import { MessageItem } from './MessageItem';
import SuggestedChips from '../SuggestedChips';
import { ChatMessage, CitationData } from './types/chat.types';
import { MinimalDocument } from '../../types';
import type { SuggestedStudioTask } from './hooks/useSuggestedContent';

interface MessageListProps {
  messages: ChatMessage[];
  isTyping: boolean;
  processingStatus?: string | null;
  processingCode?: string | null;
  lastMessageRef?: RefObject<HTMLDivElement>;
  onCopy: (content: string) => void;
  onSave: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete: (id: string) => void;
  onRate?: (id: string, rating: 1 | 5, feedback?: string) => void;
  onRegenerate?: (id: string) => void;
  onCitationClick?: (citation: CitationData) => void;
  onChunkClick?: (citation: CitationData) => void;
  onStudioResultOpen?: (result: any) => void;
  expandedReferences: Set<string>;
  expandedContext: Set<string>;
  expandedUsageStats: Set<string>;
  onToggleExpansion: (type: 'refs' | 'context' | 'stats', id: string) => void;
  isAdmin?: boolean;
  availableDocuments: MinimalDocument[];
  selectedMinimalDocuments: MinimalDocument[] | null;
  createCitationComponents: (message: ChatMessage) => any;
  // Suggested content (questions + tasks unified)
  suggestedQuestions?: string[];
  isLoadingSuggestions?: boolean;
  onSuggestedQuestionClick?: (question: string) => void;
  showSuggestedQuestions?: boolean;
  suggestedTasks?: SuggestedStudioTask[];
  isLoadingTasks?: boolean;
  onSuggestedTaskClick?: (task: SuggestedStudioTask) => void;
  showSuggestedTasks?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isTyping,
  onCopy,
  onSave,
  onEdit,
  onDelete,
  onRate,
  onRegenerate,
  onCitationClick,
  onChunkClick,
  onStudioResultOpen,
  expandedReferences,
  expandedContext,
  expandedUsageStats,
  onToggleExpansion,
  isAdmin = false,
  availableDocuments,
  selectedMinimalDocuments,
  createCitationComponents,
  processingStatus,
  processingCode,
  lastMessageRef,
  suggestedQuestions = [],
  isLoadingSuggestions = false,
  onSuggestedQuestionClick,
  showSuggestedQuestions = false,
  suggestedTasks = [],
  isLoadingTasks = false,
  onSuggestedTaskClick,
  showSuggestedTasks = false,
}) => {
  if (messages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <Bot className="w-12 h-12 mx-auto text-nb-text-secondary mb-4" />
          <h3 className="text-lg font-medium mb-2">¡Bienvenido!</h3>
          <p className="text-nb-text-secondary text-sm">
            Selecciona documentos y empieza a chatear
          </p>
        </div>
      </div>
    );
  }

  // Find the last user message to anchor scroll there
  const lastUserMsgIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) return i;
    }
    return -1;
  })();

  return (
    <>
      <div className="space-y-4" aria-live="polite" aria-relevant="additions" role="log">
        {messages.map((message, index) => {
          return (
            <div
              key={message.id}
              ref={index === lastUserMsgIndex ? lastMessageRef : undefined}
              className={`flex ${
                message.isUser ? 'justify-end' : 'justify-start items-start gap-2'
              }`}
            >
              {!message.isUser && (
                <Sparkles className="w-5 h-5 text-nb-accent flex-shrink-0 mt-[14px]" />
              )}
              <MessageItem
                message={message}
                expandedReferences={expandedReferences}
                expandedContext={expandedContext}
                expandedUsageStats={expandedUsageStats}
                onToggleExpansion={onToggleExpansion}
                onCopy={onCopy}
                onSave={onSave}
                onEdit={onEdit}
                onDelete={onDelete}
                onRate={onRate}
                onRegenerate={onRegenerate}
                onCitationClick={onCitationClick}
                onChunkClick={onChunkClick}
                onStudioResultOpen={onStudioResultOpen}
                availableDocuments={availableDocuments}
                selectedMinimalDocuments={selectedMinimalDocuments}
                createCitationComponents={createCitationComponents}
                isAdmin={isAdmin}
              />
            </div>
          );
        })}
      </div>

      {/* TypingIndicator - Shimmer skeleton while waiting for streaming */}
      {(() => {
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        const hasStreamingMessage = lastMessage?.isStreaming || false;
        return (
          <TypingIndicator
            isVisible={isTyping && !hasStreamingMessage}
            statusText={processingStatus}
            statusCode={processingCode}
          />
        );
      })()}

      {/* Suggested chips — questions + tasks unified, flat layout */}
      {!isTyping && (
        <SuggestedChips
          questions={showSuggestedQuestions ? suggestedQuestions : []}
          tasks={showSuggestedTasks ? suggestedTasks : []}
          onQuestionClick={onSuggestedQuestionClick}
          onTaskClick={onSuggestedTaskClick}
          isLoadingQuestions={showSuggestedQuestions && isLoadingSuggestions}
          isLoadingTasks={showSuggestedTasks && isLoadingTasks}
        />
      )}
    </>
  );
};

export default React.memo(MessageList);
